import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useLanguage } from "../context/LanguageContext";
import { Volume2 } from "lucide-react";
import SmartSathiChatbot from "../components/SmartSathiChatbot";


export default function Home({ user }) {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);

  const services = [
    {
      id: "health-schemes",
      icon: "🫀",
      title: t("healthSchemes"),
      color: "#007BFF",
      image: "https://images.unsplash.com/photo-1659353888906-adb3e0041693",
    },
    {
      id: "education-support",
      icon: "🎓",
      title: t("educationSupport"),
      color: "#00A859",
      image: "https://images.unsplash.com/flagged/photo-1574097656146-0b43b7660cb6",
    },
    {
      id: "id-services",
      icon: "🆔",
      title: t("idServices"),
      color: "#FFA500",
      image: "https://images.pexels.com/photos/13657444/pexels-photo-13657444.jpeg",
    },
    {
      id: "finance-dbt",
      icon: "💰",
      title: t("financeDBT"),
      color: "#007BFF",
      image: "https://images.pexels.com/photos/6694543/pexels-photo-6694543.jpeg",
    },
    {
      id: "learning",
      icon: "🎥",
      title: t("learningHub"),
      color: "#00A859",
      image: "https://images.unsplash.com/photo-1707721690544-781fe6ede937",
    },
  ];

  const speakGreeting = () => {
    if (!("speechSynthesis" in window)) return;

    setIsPlaying(true);

    const utterance = new SpeechSynthesisUtterance(t("greeting"));
    utterance.lang =
      language === "hindi"
        ? "hi-IN"
        : language === "tamil"
        ? "ta-IN"
        : language === "marathi"
        ? "mr-IN"
        : "en-IN";

    utterance.onend = () => setIsPlaying(false);
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
    }
  }, [user, setLanguage]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <header className="bg-white shadow sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🤝</span>
            <div>
              <h1 className="text-2xl font-bold text-blue-600">SmartSathi</h1>
              <p className="text-xs text-green-600">स्मार्ट साथी</p>
            </div>
          </div>

          <div className="flex gap-4 items-center">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी</SelectItem>
                <SelectItem value="marathi">मराठी</SelectItem>
                <SelectItem value="tamil">தமிழ்</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={handleLogout}>
              {t("logout")}
            </Button>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <Button
          size="lg"
          className="mt-10"
          style={{ background: "#007BFF" }}
          onClick={() => navigate("/gov-search")}
        >
        🔍 Government Search Portal
        </Button>
        {/* GREETING */}
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold mb-4">
            {t("welcome")}, {user?.name}! 👋
          </h2>

          <p className="text-gray-600 mb-6">{t("greeting")}</p>

          <Button
            size="lg"
            onClick={speakGreeting}
            className="px-8"
            style={{ background: isPlaying ? "#FFA500" : "#007BFF" }}
          >
            <Volume2 className="mr-2" />
            {isPlaying ? t("stopVoice") : t("startVoice")}
          </Button>
        </div>


        {/* SERVICE CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <Card
              key={service.id}
              className="cursor-pointer overflow-hidden"
              onClick={() =>
                service.id === "learning"
                  ? navigate("/learning")
                  : navigate(`/guide/${service.id}`)
              }
            >
              <div className="relative h-44">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 text-5xl">
                  {service.icon}
                </div>
              </div>

              <div className="p-6">
                <h3
                  className="text-xl font-bold mb-3"
                  style={{ color: service.color }}
                >
                  {service.title}
                </h3>

                <Button className="w-full" style={{ background: service.color }}>
                  {t("viewGuide")}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* ADMIN */}
        <div className="text-center mt-16">
          <Button variant="outline" size="lg" onClick={() => navigate("/admin")}>
            {t("admin")}
          </Button>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="bg-white border-t py-6 mt-16 text-center text-gray-600">
        {t("footerText")}
      </footer>
    </div>
  );
}
